/**
 * アンケート (内蔵 Google Forms 相当) の型定義 (docs/04 §4-3, REQ-4.2.8〜10)。
 *
 * 設計データは `/districts/{districtId}/surveys/{surveyId}` に保存。
 * 回答は `/districts/{districtId}/surveys/{surveyId}/responses/{participantUid}` (1 人 1 回・編集可)。
 *
 * 旧 `old/packages/shared-types/src/survey.ts` を admin 第 22 弾 (Phase 5 L2) で v2 に踏襲。
 * 旧との差分:
 *   - `SurveyExport` (JSON 入出力 schema) は本 sprint scope 外 (GAP-A10 別 sprint)。
 *   - master 連携メタ (`sourceMasterSurveyId` / `linkedToMaster` / `masterVersion`) はそのまま温存
 *     (master surveys CRUD は別 sprint だが、地区 survey schema 上は forward-compatible に保持)。
 */
/**
 * 「その他」(radio / checkbox の `allowOther`) を選んだ回答の保存値。記入内容は `otherText` に入る。
 * 普通の選択肢と区別できなくなるため、`allowOther` の質問ではこの文言の選択肢を置かない。
 */
export const SURVEY_OTHER_VALUE = 'その他';
/** 質問タイプの順序付き列挙 (UI の dropdown 順)。 */
export const SURVEY_QUESTION_TYPES = [
    'short_text',
    'long_text',
    'radio',
    'dropdown',
    'checkbox',
    'linear_scale',
    'rating',
    'date',
    'time',
    'number',
];
/** 質問タイプの表示ラベル (ja-JP)。 */
export const SURVEY_QUESTION_TYPE_LABELS = {
    short_text: '短文',
    long_text: '段落(長文)',
    radio: 'ラジオ(単一選択)',
    dropdown: 'プルダウン',
    checkbox: 'チェックボックス(複数選択)',
    linear_scale: '均等目盛',
    rating: '5段階評価(星)',
    date: '日付',
    time: '時刻',
    number: '数値',
};
/**
 * 新規アンケートのテンプレート生成 (旧 helper 踏襲)。
 * actorUid は createdBy/updatedBy に焼き込み、Firestore 書込時点で固定。
 */
export function createEmptySurvey(id, actorUid) {
    const now = new Date().toISOString();
    return {
        id,
        title: '新しいアンケート',
        description: '',
        status: 'draft',
        questions: [],
        collectParticipantProfile: true,
        allowEdit: true,
        schemaVersion: 1,
        responseCount: 0,
        createdAt: now,
        updatedAt: now,
        createdBy: actorUid,
        updatedBy: actorUid,
        version: 0,
    };
}
/** 質問タイプから初期値を生成 (旧 helper 踏襲)。 */
export function createDefaultQuestion(type, id) {
    const base = { id, title: '', required: false };
    switch (type) {
        case 'short_text':
            return { ...base, type: 'short_text' };
        case 'long_text':
            return { ...base, type: 'long_text' };
        case 'radio':
            return { ...base, type: 'radio', options: [{ label: '選択肢 1' }] };
        case 'dropdown':
            return { ...base, type: 'dropdown', options: [{ label: '選択肢 1' }] };
        case 'checkbox':
            return { ...base, type: 'checkbox', options: [{ label: '選択肢 1' }] };
        case 'linear_scale':
            return { ...base, type: 'linear_scale', min: 1, max: 5 };
        case 'rating':
            return { ...base, type: 'rating', max: 5 };
        case 'date':
            return { ...base, type: 'date' };
        case 'time':
            return { ...base, type: 'time' };
        case 'number':
            return { ...base, type: 'number' };
    }
}
/**
 * 選択式の質問の選択肢の問題を 1 つ返す (保存前の確認用)。問題が無ければ null。
 *   - 選択肢が 0 件
 *   - 同じ文言の選択肢が 2 つ以上 (回答を文言で保存するため区別できない)
 *   - 「その他」記入欄付き (allowOther) なのに「その他」という選択肢もある
 */
export function findSurveyOptionProblem(questions) {
    for (const q of questions) {
        if (q.type !== 'radio' && q.type !== 'dropdown' && q.type !== 'checkbox')
            continue;
        const name = q.title || '無題の質問';
        if (q.options.length === 0) {
            return { question: q, message: `「${name}」の選択肢を 1 つ以上入力してください` };
        }
        const seen = new Set();
        for (const o of q.options) {
            if (seen.has(o.label)) {
                return { question: q, message: `「${name}」に同じ選択肢「${o.label}」が 2 つあります` };
            }
            seen.add(o.label);
        }
        if (q.type !== 'dropdown' && q.allowOther && seen.has(SURVEY_OTHER_VALUE)) {
            return {
                question: q,
                message: `「${name}」は「${SURVEY_OTHER_VALUE}（記入欄付き）」を使う設定なので、選択肢に「${SURVEY_OTHER_VALUE}」を入れないでください`,
            };
        }
    }
    return null;
}
/** 質問タイプ切替 (旧 helper 踏襲、id・title・description・required を保ちつつ type を入れ替え)。 */
export function changeQuestionType(q, nextType) {
    const fresh = createDefaultQuestion(nextType, q.id);
    return {
        ...fresh,
        title: q.title,
        // 補足説明が無い質問で description: undefined を作らない (Firestore は undefined を保存できない)。
        ...(q.description !== undefined ? { description: q.description } : {}),
        required: q.required,
    };
}
/**
 * 保存用に質問から値が undefined の項目を取り除く (2026-10-08)。
 * Firestore は書き込むデータのどこかに undefined があると保存全体をエラーにする。編集画面では
 * 数値欄を空にしたとき (最小・最大選択数 / 最大文字数 / 数値の範囲・刻み) に undefined が入るため、
 * アンケート (地区) とテンプレート (マスタ) の保存前に必ず通す。項目を消すだけなので「設定なし」と同じ。
 */
export function sanitizeSurveyQuestions(questions) {
    return questions.map((q) => Object.fromEntries(Object.entries(q)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [
        k,
        Array.isArray(v)
            ? v.map((o) => o && typeof o === 'object'
                ? Object.fromEntries(Object.entries(o).filter(([, x]) => x !== undefined))
                : o)
            : v,
    ])));
}
//# sourceMappingURL=survey.js.map